import { createSlice } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';
import { vehiclesServices, fleetVehiclesServices } from '@services';
import { GENERIC_ERROR_MESSAGE } from '@constants';
import { asyncForEach } from '@utils';
import { handleVehicleData, handleVehiclesBySku } from './vehicle.handlers';

export const VEHICLES_BY_SKU_PAGE_SIZE = 50;

// Initial state
const initialState = {
  loading: false,
  hasErrors: false,
  errorMessage: '',
  vehicle: {
    vehiclesBySku: {
      loading: false,
      data: [],
      errorMessage: '',
      total: 0,
      page: 1,
      pageSize: VEHICLES_BY_SKU_PAGE_SIZE,
    },
  },
  postLoading: false,
  postHasErrors: false,
  postErrorMessage: '',
  postSuccess: false,
  filters: {
    modalFiltersRules: undefined,
    modalConditions: undefined,
  },
};
// Actual Slice
export const vehiclesSlice = createSlice({
  name: 'vehicles',
  initialState,
  reducers: {
    fetchVehicle: (state) => {
      state.loading = true;
      state.hasErrors = false;
      state.errorMessage = '';
      state.vehicle = initialState.vehicle;
      state.postLoading = false;
      state.postHasErrors = false;
      state.postSuccess = false;
      state.postErrorMessage = '';
    },
    fetchVehicleSuccess: (state, { payload }) => {
      state.vehicle = handleVehicleData(payload);
      state.loading = false;
      state.hasErrors = false;
      state.errorMessage = '';
    },
    fetchVehicleBySku: (state) => {
      if (!state.vehicle.vehiclesBySku) {
        state.vehicle.vehiclesBySku = { ...initialState.vehicle.vehiclesBySku };
      }
      state.vehicle.vehiclesBySku.errorMessage = '';
      state.vehicle.vehiclesBySku.loading = true;
    },
    fetchVehicleBySkuSuccess: (state, { payload }) => {
      state.vehicle.vehiclesBySku.data = handleVehiclesBySku(payload.data);
      state.vehicle.vehiclesBySku.errorMessage = '';
      state.vehicle.vehiclesBySku.loading = false;
      state.vehicle.vehiclesBySku.total = payload?.meta?.total || 0;
      state.vehicle.vehiclesBySku.page = payload?.meta?.page;
      state.vehicle.vehiclesBySku.pageSize = payload?.meta?.pageSize;
    },
    fetchVehicleBySkuFailure: (state, { payload }) => {
      state.vehicle.vehiclesBySku.errorMessage =
        payload || GENERIC_ERROR_MESSAGE;
      state.vehicle.vehiclesBySku.loading = false;
    },
    fetchVehicleFailure: (state, { payload }) => {
      state.loading = false;
      state.hasErrors = true;
      state.errorMessage = payload || GENERIC_ERROR_MESSAGE;
    },
    resetVehicle: (state) => {
      state.postLoading = false;
      state.postHasErrors = false;
      state.postErrorMessage = '';
      state.postSuccess = false;
      const vehicle = { ...state.vehicle };
      if (vehicle?.vehicle) {
        vehicle.vehicle = initialState.vehicle;
      }
      state.vehicle = vehicle;
    },
    postVehicle: (state) => {
      state.postLoading = true;
      state.postHasErrors = false;
      state.postErrorMessage = '';
      state.postSuccess = false;
    },
    postVehicleSuccess: (state) => {
      state.postLoading = false;
      state.postHasErrors = false;
      state.postErrorMessage = '';
      state.postSuccess = true;
    },
    postVehicleFailure: (state, { payload }) => {
      state.postLoading = false;
      state.postHasErrors = true;
      state.postErrorMessage = payload;
    },
    deleteVehicleImageSuccess: (state, { payload }) => {
      const vehicle = { ...state?.vehicle };

      if (vehicle?.vehicle?.images?.length) {
        const images = vehicle?.vehicle?.images?.filter((image) => {
          return image?.id !== payload;
        });
        vehicle.vehicle.images = images;
      }
      state.vehicle = vehicle;
      state.postLoading = false;
      state.postHasErrors = false;
      state.postErrorMessage = '';
    },

    deleteVehicleFileSuccess: (state, { payload }) => {
      const vehicle = { ...state?.vehicle };

      if (vehicle?.vehicle?.docs?.length) {
        const docs = vehicle?.vehicle?.docs?.filter((doc) => {
          return doc?.id !== payload;
        });
        vehicle.vehicle.docs = docs;
      }
      state.vehicle = vehicle;
      state.postLoading = false;
      state.postHasErrors = false;
      state.postErrorMessage = '';
    },
    filtersModalConditionChangeVehiclesSuccess: (state, { payload }) => {
      state.filters.modalConditions = payload;
      if (!payload?.rules?.length) {
        state.filters.modalFiltersRules = undefined;
      }
    },
    filtersModalRulesVehiclesChangeSuccess: (state, { payload }) => {
      state.filters.modalFiltersRules = payload;
    },
    resetModalChangeVehiclesSuccess: (state) => {
      state.filters.modalConditions = undefined;
      state.filters.modalFiltersRules = undefined;
    },
    // Special reducer for hydrating the state. Special case for next-redux-wrapper
    extraReducers: {
      [HYDRATE]: (state, action) => {
        return {
          ...state,
          ...action.payload.errors,
        };
      },
    },
  },
});

export const {
  fetchVehicle,
  fetchVehicleSuccess,
  fetchVehicleFailure,
  fetchVehicleBySkuSuccess,
  fetchVehicleBySku,
  fetchVehicleBySkuFailure,
  postVehicle,
  postVehicleSuccess,
  postVehicleFailure,
  resetVehicle,
  deleteVehicleImageSuccess,
  deleteVehicleFileSuccess,
  filtersModalConditionChangeVehiclesSuccess,
  filtersModalRulesVehiclesChangeSuccess,
  resetModalChangeVehiclesSuccess,
} = vehiclesSlice.actions;

export const selectVehiclesState = (state) => state.vehicles;

export const getVehiclesBySku = (
  vehicleId,
  params = { page: 1, pageSize: VEHICLES_BY_SKU_PAGE_SIZE }
) => {
  return async (dispatch) => {
    dispatch(fetchVehicleBySku());
    const page = parseInt(params?.page, 10);
    const pageSize = parseInt(params?.pageSize, 10);

    try {
      if (vehicleId) {
        const response = await vehiclesServices?.getVehiclesBySku(
          vehicleId,
          params
        );
        response.meta.page = page;
        response.meta.pageSize = pageSize;
        dispatch(fetchVehicleBySkuSuccess(response));
      }
    } catch (error) {
      dispatch(fetchVehicleBySkuFailure(error));
    }
  };
};

export const getVehicle = (vehicleId) => {
  return async (dispatch) => {
    dispatch(fetchVehicle());
    try {
      const data = {};
      if (vehicleId) {
        data.vehicle = await vehiclesServices?.getVehicle(vehicleId);
      } else {
        data.vehicle = initialState.vehicle;
      }

      data.enums = await vehiclesServices?.getVehicleEnums();
      data.colors = await fleetVehiclesServices?.getVehicleColors();
      data.providers = await fleetVehiclesServices?.getVehicleProviders();
      data.models = await fleetVehiclesServices?.getVehiclesModels();
      dispatch(fetchVehicleSuccess(data));
    } catch (error) {
      dispatch(fetchVehicleFailure(error));
    }
  };
};

export const addVehicle = (payload, canSavePricing) => {
  return async (dispatch) => {
    dispatch(postVehicle());

    try {
      const { skuId } = payload.form;

      await vehiclesServices.postVehicle(payload.form);

      if (payload.pricing?.length && canSavePricing) {
        await asyncForEach(payload?.pricing, async (prices) => {
          if (!prices?.insert) {
            await vehiclesServices.updatePricing(skuId, prices?.id, prices);
          } else {
            await vehiclesServices.addPricing(skuId, [prices]);
          }
        });
      }
      if (payload.docs?.length) {
        await asyncForEach(payload?.docs, async (doc) => {
          vehiclesServices.postVehicleFile(skuId, doc.file);
        });
      }

      dispatch(postVehicleSuccess());
    } catch (error) {
      dispatch(postVehicleFailure(error));
    }
  };
};

export const editVehicle = (payload, canSavePricing) => {
  return async (dispatch) => {
    dispatch(postVehicle());

    try {
      const { skuId } = payload.form;
      const { primaryImageId } = payload;
      const data = {
        ...payload.form,
        images: [...payload.images],
        documents: [...payload.docs],
      };
      await vehiclesServices.updateVehicle(skuId, data);

      if (payload.pricing?.length && canSavePricing) {
        await asyncForEach(payload?.pricing, async (prices) => {
          if (!prices?.insert) {
            await vehiclesServices.updatePricing(skuId, prices?.id, prices);
          } else {
            await vehiclesServices.addPricing(skuId, [prices]);
          }
        });
      }
      if (primaryImageId) {
        await vehiclesServices.postVehiclePrimaryImage(skuId, primaryImageId);
      }

      dispatch(postVehicleSuccess());
    } catch (error) {
      dispatch(postVehicleFailure(error));
    }
  };
};

export const deleteVehicleImage = (payload) => {
  return async (dispatch) => {
    dispatch(postVehicle());

    try {
      await vehiclesServices.deleteVehicleImage(payload);

      dispatch(deleteVehicleImageSuccess(payload?.id));
    } catch (error) {
      dispatch(postVehicleFailure(error));
    }
  };
};

export const deleteVehicleFile = (payload) => {
  return async (dispatch) => {
    dispatch(postVehicle());

    try {
      await vehiclesServices.deleteVehicleFile(payload);

      dispatch(deleteVehicleFileSuccess(payload?.id));
    } catch (error) {
      dispatch(postVehicleFailure(error));
    }
  };
};

export const filtersModalConditionVehiclesChange = (conditions) => {
  return (dispatch) => {
    dispatch(filtersModalConditionChangeVehiclesSuccess(conditions));
  };
};

export const filtersModalRulesVehiclesChange = (rules) => {
  return (dispatch) => {
    dispatch(filtersModalRulesVehiclesChangeSuccess(rules));
  };
};

export const resetModalVehiclesChange = () => {
  return (dispatch) => {
    dispatch(resetModalChangeVehiclesSuccess());
  };
};

export default vehiclesSlice.reducer;
